import type { Color } from "../common/color.js";
import { RenderTarget } from "../engine/RenderTarget.js";
import type { GpuContext } from "../engine/GpuContext.js";
import type { Camera2D } from "./Camera2D.js";
import { Node2D, Sprite } from "./Node2D.js";
import { rectsOverlap, transformRect, type Rect2D } from "./math2d.js";
import { SpriteBatch } from "./SpriteBatch.js";
import { TileMap } from "./TileMap.js";

export interface Renderer2DOptions {
  width: number;
  height: number;
  /** Behind everything. Transparent by default, so the UI shows through. */
  clear?: Color;
  /** Sample the finished scene smoothly. Off for pixel art. */
  smooth?: boolean;
  /** Skip nodes whose bounds miss the camera. */
  cull?: boolean;
}

const TRANSPARENT: Color = { r: 0, g: 0, b: 0, a: 0 };

/**
 * Draws a 2D scene into an off-screen target the UI can then show.
 *
 * Traversal is a plain depth-first walk in `z` order: a 2D renderer has no
 * depth buffer, so the order things are submitted *is* the order they stack,
 * and that has to be the scene's order rather than whatever grouping would
 * batch best.
 *
 * Culling is by world-space bounding box against the camera's visible
 * rectangle. It is worth doing before the batch rather than after because a
 * culled sprite costs nothing at all — no instance, no upload, no draw.
 */
export class Renderer2D {
  readonly gpu: GpuContext;
  readonly target: RenderTarget;
  readonly batch: SpriteBatch;

  clear: Color;
  cull: boolean;

  #culled = 0;
  #drawn = 0;

  constructor(gpu: GpuContext, options: Renderer2DOptions) {
    this.gpu = gpu;
    this.clear = options.clear ?? TRANSPARENT;
    this.cull = options.cull ?? true;
    this.target = new RenderTarget(gpu, {
      width: options.width,
      height: options.height,
      depth: false,
      smooth: options.smooth ?? true,
      label: "webwin.engine2d",
    });
    this.batch = new SpriteBatch(gpu);
  }

  get width(): number {
    return this.target.width;
  }

  get height(): number {
    return this.target.height;
  }

  /** Sprites drawn and skipped on the last frame. */
  get stats(): { drawn: number; culled: number; draws: number } {
    return { drawn: this.#drawn, culled: this.#culled, draws: this.batch.drawCallCount };
  }

  resize(width: number, height: number): void {
    this.target.resize(width, height);
  }

  /** Draw `scene` through `camera` into the target. */
  render(scene: Node2D, camera: Camera2D): void {
    camera.setViewport(this.target.width, this.target.height);
    this.#culled = 0;
    this.#drawn = 0;

    this.batch.begin(camera.viewMatrix, this.target.width, this.target.height);
    const visible = this.cull ? camera.visibleBounds : null;
    this.#collect(scene, visible);

    this.target.begin(this.clear);
    this.batch.flush();
  }

  /** Walk the tree in paint order, pushing whatever the camera can see. */
  #collect(node: Node2D, visible: Rect2D | null): void {
    if (!node.visible) return;

    if (node instanceof Sprite) this.#pushSprite(node, visible);
    else if (node instanceof TileMap) this.#pushTiles(node, visible);

    for (const child of node.sortedChildren()) this.#collect(child, visible);
  }

  #pushSprite(sprite: Sprite, visible: Rect2D | null): void {
    if (visible && !rectsOverlap(transformRect(sprite.worldMatrix, sprite.localBounds()), visible)) {
      this.#culled++;
      return;
    }
    const m = sprite.worldMatrix;
    // The anchor shifts the quad off the node's origin before rotation, so it
    // has to go through the world matrix rather than being added afterwards.
    const offsetX = (0.5 - sprite.anchor.x) * sprite.width;
    const offsetY = (0.5 - sprite.anchor.y) * sprite.height;
    const center = {
      x: m.a * offsetX + m.c * offsetY + m.tx,
      y: m.b * offsetX + m.d * offsetY + m.ty,
    };
    // Scale and rotation come out of the matrix rather than the node, so a
    // sprite inherits both from every ancestor.
    const scaleX = Math.hypot(m.a, m.b);
    const scaleY = Math.hypot(m.c, m.d);

    this.batch.push(sprite.texture, {
      x: center.x,
      y: center.y,
      width: sprite.width * scaleX,
      height: sprite.height * scaleY,
      rotation: Math.atan2(m.b, m.a),
      frame: sprite.frame,
      tint: sprite.tint,
      opacity: sprite.opacity,
    });
    this.#drawn++;
  }

  #pushTiles(map: TileMap, visible: Rect2D | null): void {
    const m = map.worldMatrix;
    const rotation = Math.atan2(m.b, m.a);
    const scaleX = Math.hypot(m.a, m.b);
    const scaleY = Math.hypot(m.c, m.d);

    for (const tile of map.visibleTiles(visible)) {
      const centerX = tile.x + map.tileWidth / 2;
      const centerY = tile.y + map.tileHeight / 2;
      this.batch.push(map.texture, {
        x: m.a * centerX + m.c * centerY + m.tx,
        y: m.b * centerX + m.d * centerY + m.ty,
        width: map.tileWidth * scaleX,
        height: map.tileHeight * scaleY,
        rotation,
        frame: tile.frame,
        tint: map.tint,
        opacity: map.opacity,
      });
      this.#drawn++;
    }
  }

  dispose(): void {
    this.batch.dispose();
    this.target.dispose();
  }
}
